import React, { useState, useEffect, useCallback } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faEdit,
  faTrash,
  faCheckCircle,
  faSpinner,
  faClock,
  faBullseye,
  faFilter,
  faSearch,
  faTimes,
  faCalendar,
  faStickyNote,
  faUser,
  faChevronDown,
  faChevronUp,
  faTrophy,
} from "@fortawesome/free-solid-svg-icons";
import apiService from "../../ApiService";

const GoalsManagement = () => {
  const [goals, setGoals] = useState([]);
  const [filteredGoals, setFilteredGoals] = useState([]);
  const [activeGoals, setActiveGoals] = useState([]);
  const [completedGoals, setCompletedGoals] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editingGoal, setEditingGoal] = useState(null);
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterCategory, setFilterCategory] = useState("all");
  const [filterUser, setFilterUser] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [showCompletedSection, setShowCompletedSection] = useState(false);

  const [formData, setFormData] = useState({
    title: "",
    description: "",
    category: "education",
    target_date: "",
    priority: "medium",
    progress_percentage: 0,
    status: "not-started",
    notes: "",
    assigned_to: "Kavi",
  });

  const users = [
    { value: "Kavi", label: "👨‍💼 Kavi", color: "blue" },
    { value: "Senthil", label: "👨‍💻 Senthil", color: "green" },
    { value: "Renu", label: "👩‍💼 Renu", color: "purple" },
    { value: "Veda", label: "👩‍🎓 Veda", color: "pink" },
  ];

  const categories = [
    { value: "education", label: "📚 Education", color: "blue" },
    { value: "career", label: "💼 Career", color: "purple" },
    { value: "health", label: "💪 Health", color: "green" },
    { value: "finance", label: "💰 Finance", color: "yellow" },
    { value: "personal", label: "🎯 Personal", color: "pink" },
    { value: "other", label: "📌 Other", color: "gray" },
  ];

  const priorities = [
    { value: "low", label: "Low", color: "gray" },
    { value: "medium", label: "Medium", color: "blue" },
    { value: "high", label: "High", color: "orange" },
    { value: "urgent", label: "Urgent", color: "red" },
  ];

  const statuses = [
    { value: "not-started", label: "Not Started", color: "gray" },
    { value: "in-progress", label: "In Progress", color: "blue" },
    { value: "completed", label: "Completed", color: "green" },
    { value: "on-hold", label: "On Hold", color: "yellow" },
    { value: "cancelled", label: "Cancelled", color: "red" },
  ];

  useEffect(() => {
    fetchGoals();
    fetchSummary();
  }, []);

  const applyFilters = useCallback(() => {
    let filtered = [...goals];

    if (filterStatus !== "all") {
      filtered = filtered.filter((goal) => goal.status === filterStatus);
    }

    if (filterCategory !== "all") {
      filtered = filtered.filter((goal) => goal.category === filterCategory);
    }

    if (filterUser !== "all") {
      filtered = filtered.filter((goal) => goal.assigned_to === filterUser);
    }

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (goal) =>
          goal.title.toLowerCase().includes(query) ||
          goal.description.toLowerCase().includes(query),
      );
    }

    setFilteredGoals(filtered);

    // Separate active and completed goals
    const active = filtered.filter((goal) => goal.status !== "completed");
    const completed = filtered.filter((goal) => goal.status === "completed");
    setActiveGoals(active);
    setCompletedGoals(completed);
  }, [goals, filterStatus, filterCategory, filterUser, searchQuery]);

  useEffect(() => {
    applyFilters();
  }, [applyFilters]);

  const fetchGoals = async () => {
    setLoading(true);
    setError("");
    try {
      const data = await apiService.getGoals();
      setGoals(Array.isArray(data) ? data : data.goals || []);
    } catch (err) {
      setError("Failed to fetch goals");
      console.error(err);
    }
    setLoading(false);
  };

  const fetchSummary = async () => {
    try {
      const data = await apiService.getGoals({ action: "summary" });
      setSummary(data);
    } catch (err) {
      console.error("Failed to fetch summary:", err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!formData.title || !formData.target_date) {
      setError("Title and Target Date are required");
      return;
    }

    try {
      if (editingGoal) {
        await apiService.updateGoal(editingGoal.id, formData);
      } else {
        await apiService.createGoal(formData);
      }
      await fetchGoals();
      await fetchSummary();
      resetForm();
    } catch (err) {
      setError(editingGoal ? "Failed to update goal" : "Failed to create goal");
      console.error(err);
    }
  };

  const handleEdit = (goal) => {
    setEditingGoal(goal);
    setFormData({
      title: goal.title || "",
      description: goal.description || "",
      category: goal.category || "education",
      target_date: goal.target_date || "",
      priority: goal.priority || "medium",
      progress_percentage: goal.progress_percentage || 0,
      status: goal.status || "not-started",
      notes: goal.notes || "",
      assigned_to: goal.assigned_to || "Kavi",
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this goal?")) return;

    try {
      await apiService.deleteGoal(id);
      await fetchGoals();
      await fetchSummary();
    } catch (err) {
      setError("Failed to delete goal");
      console.error(err);
    }
  };

  const resetForm = () => {
    setFormData({
      title: "",
      description: "",
      category: "education",
      target_date: "",
      priority: "medium",
      progress_percentage: 0,
      status: "not-started",
      notes: "",
      assigned_to: "Kavi",
    });
    setEditingGoal(null);
    setShowForm(false);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const getCategoryColor = (category) => {
    return categories.find((c) => c.value === category)?.color || "gray";
  };

  const getPriorityColor = (priority) => {
    return priorities.find((p) => p.value === priority)?.color || "gray";
  };

  const getStatusColor = (status) => {
    return statuses.find((s) => s.value === status)?.color || "gray";
  };

  const getProgressColor = (progress) => {
    if (progress === 0) return "gray";
    if (progress < 30) return "red";
    if (progress < 70) return "yellow";
    return "green";
  };

  const getUserColor = (user) => {
    return users.find((u) => u.value === user)?.color || "gray";
  };

  const getDaysRemaining = (targetDate) => {
    const today = new Date();
    const target = new Date(targetDate);
    const diffTime = target - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const renderGoalCard = (goal, isCompleted = false) => {
    const daysRemaining = getDaysRemaining(goal.target_date);
    const isOverdue = daysRemaining < 0 && !isCompleted;

    return (
      <div
        key={goal.id}
        className={`rounded-2xl shadow-lg border-2 p-6 transition-all hover:shadow-xl hover:scale-[1.01] ${
          isCompleted
            ? "bg-gradient-to-r from-green-50 to-emerald-50 border-green-300"
            : isOverdue
              ? "bg-red-50 border-red-300"
              : "bg-white border-gray-200"
        }`}
      >
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1">
            <div className="flex items-center flex-wrap gap-2 mb-3">
              {isCompleted && (
                <FontAwesomeIcon
                  icon={faCheckCircle}
                  className="text-green-600 text-xl"
                />
              )}
              <h3 className="text-xl font-bold text-gray-900">{goal.title}</h3>
              <span
                className={`px-3 py-1 bg-${getUserColor(
                  goal.assigned_to,
                )}-100 text-${getUserColor(
                  goal.assigned_to,
                )}-700 rounded-full text-xs font-medium flex items-center`}
              >
                <FontAwesomeIcon icon={faUser} className="mr-1" />
                {users.find((u) => u.value === goal.assigned_to)?.label ||
                  goal.assigned_to}
              </span>
              <span
                className={`px-3 py-1 bg-${getCategoryColor(
                  goal.category,
                )}-100 text-${getCategoryColor(
                  goal.category,
                )}-700 rounded-full text-xs font-medium`}
              >
                {categories.find((c) => c.value === goal.category)?.label ||
                  goal.category}
              </span>
              {!isCompleted && (
                <>
                  <span
                    className={`px-3 py-1 bg-${getPriorityColor(
                      goal.priority,
                    )}-100 text-${getPriorityColor(
                      goal.priority,
                    )}-700 rounded-full text-xs font-medium`}
                  >
                    {goal.priority}
                  </span>
                  <span
                    className={`px-3 py-1 bg-${getStatusColor(
                      goal.status,
                    )}-100 text-${getStatusColor(
                      goal.status,
                    )}-700 rounded-full text-xs font-medium`}
                  >
                    {statuses.find((s) => s.value === goal.status)?.label ||
                      goal.status}
                  </span>
                </>
              )}
              {isCompleted && (
                <span className="px-3 py-1 bg-green-600 text-white rounded-full text-xs font-bold">
                  ✓ COMPLETED
                </span>
              )}
            </div>
            {goal.description && (
              <p className="text-gray-600 mb-3">{goal.description}</p>
            )}
            <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600">
              <div className="flex items-center space-x-2">
                <FontAwesomeIcon icon={faCalendar} />
                <span>
                  {isCompleted ? "Completed:" : "Target:"}{" "}
                  {new Date(goal.target_date).toLocaleDateString()}
                </span>
              </div>
              {!isCompleted && (
                <div className="flex items-center space-x-2">
                  <FontAwesomeIcon icon={faClock} />
                  <span className={isOverdue ? "text-red-600 font-medium" : ""}>
                    {isOverdue
                      ? `Overdue by ${Math.abs(daysRemaining)} days`
                      : daysRemaining === 0
                        ? "Due today"
                        : `${daysRemaining} days remaining`}
                  </span>
                </div>
              )}
              {goal.notes && (
                <div className="flex items-center space-x-2">
                  <FontAwesomeIcon icon={faStickyNote} />
                  <span className="truncate max-w-xs">{goal.notes}</span>
                </div>
              )}
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => handleEdit(goal)}
              className="p-2 bg-blue-100 hover:bg-blue-200 text-blue-600 rounded-lg transition-colors"
              title="Edit Goal"
            >
              <FontAwesomeIcon icon={faEdit} />
            </button>
            <button
              onClick={() => handleDelete(goal.id)}
              className="p-2 bg-red-100 hover:bg-red-200 text-red-600 rounded-lg transition-colors"
              title="Delete Goal"
            >
              <FontAwesomeIcon icon={faTrash} />
            </button>
          </div>
        </div>

        {/* Progress Bar */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <span
              className={`text-sm font-medium ${
                isCompleted ? "text-green-700" : "text-gray-700"
              }`}
            >
              Progress
            </span>
            <span
              className={`text-sm font-bold ${
                isCompleted ? "text-green-700" : "text-gray-900"
              }`}
            >
              {isCompleted ? 100 : goal.progress_percentage}%
            </span>
          </div>
          <div
            className={`w-full rounded-full h-3 ${
              isCompleted ? "bg-green-200" : "bg-gray-200"
            }`}
          >
            <div
              className={`${
                isCompleted
                  ? "bg-green-600"
                  : `bg-${getProgressColor(goal.progress_percentage)}-500`
              } h-3 rounded-full transition-all`}
              style={{
                width: `${isCompleted ? 100 : goal.progress_percentage}%`,
              }}
            ></div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-2xl p-6 mb-6 border border-purple-200">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <div className="bg-purple-600 p-3 rounded-xl">
              <FontAwesomeIcon
                icon={faBullseye}
                className="text-white text-2xl"
              />
            </div>
            <div>
              <h2 className="text-3xl font-bold text-gray-900">
                Goals Management
              </h2>
              <p className="text-gray-600">
                Track and achieve your personal & professional goals
              </p>
            </div>
          </div>
          <button
            onClick={() => {
              resetForm();
              setShowForm(!showForm);
            }}
            className="flex items-center space-x-2 px-6 py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors shadow-lg"
          >
            <FontAwesomeIcon icon={showForm ? faTimes : faPlus} />
            <span>{showForm ? "Cancel" : "New Goal"}</span>
          </button>
        </div>

        {/* Summary Cards */}
        {summary && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
            <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total Goals</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {summary.total || 0}
                  </p>
                </div>
                <FontAwesomeIcon
                  icon={faBullseye}
                  className="text-3xl text-purple-500"
                />
              </div>
            </div>
            <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">In Progress</p>
                  <p className="text-2xl font-bold text-blue-600">
                    {summary.in_progress || 0}
                  </p>
                </div>
                <FontAwesomeIcon
                  icon={faSpinner}
                  className="text-3xl text-blue-500"
                />
              </div>
            </div>
            <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Completed</p>
                  <p className="text-2xl font-bold text-green-600">
                    {summary.completed || 0}
                  </p>
                </div>
                <FontAwesomeIcon
                  icon={faCheckCircle}
                  className="text-3xl text-green-500"
                />
              </div>
            </div>
            <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Not Started</p>
                  <p className="text-2xl font-bold text-gray-600">
                    {summary.not_started || 0}
                  </p>
                </div>
                <FontAwesomeIcon
                  icon={faClock}
                  className="text-3xl text-gray-400"
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6">
          <p className="text-red-800 font-medium">{error}</p>
        </div>
      )}

      {/* Add/Edit Form */}
      {showForm && (
        <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6 mb-6">
          <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center space-x-2">
            <FontAwesomeIcon
              icon={editingGoal ? faEdit : faPlus}
              className="text-purple-600"
            />
            <span>{editingGoal ? "Edit Goal" : "Create New Goal"}</span>
          </h3>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Goal Title *
                </label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  placeholder="e.g., Learn React Advanced Concepts"
                  required
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  rows="3"
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  placeholder="Describe your goal in detail..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Category
                </label>
                <select
                  name="category"
                  value={formData.category}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                >
                  {categories.map((cat) => (
                    <option key={cat.value} value={cat.value}>
                      {cat.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <FontAwesomeIcon icon={faUser} className="mr-2" />
                  Assign To
                </label>
                <select
                  name="assigned_to"
                  value={formData.assigned_to}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                >
                  {users.map((user) => (
                    <option key={user.value} value={user.value}>
                      {user.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Target Date *
                </label>
                <input
                  type="date"
                  name="target_date"
                  value={formData.target_date}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Priority
                </label>
                <select
                  name="priority"
                  value={formData.priority}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                >
                  {priorities.map((pri) => (
                    <option key={pri.value} value={pri.value}>
                      {pri.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Status
                </label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                >
                  {statuses.map((stat) => (
                    <option key={stat.value} value={stat.value}>
                      {stat.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Progress: {formData.progress_percentage}%
                </label>
                <input
                  type="range"
                  name="progress_percentage"
                  value={formData.progress_percentage}
                  onChange={handleChange}
                  min="0"
                  max="100"
                  step="5"
                  className="w-full"
                />
                <div className="w-full bg-gray-200 rounded-full h-3 mt-2">
                  <div
                    className={`bg-${getProgressColor(
                      formData.progress_percentage,
                    )}-500 h-3 rounded-full transition-all`}
                    style={{ width: `${formData.progress_percentage}%` }}
                  ></div>
                </div>
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Notes
                </label>
                <textarea
                  name="notes"
                  value={formData.notes}
                  onChange={handleChange}
                  rows="2"
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  placeholder="Add any additional notes or milestones..."
                />
              </div>
            </div>

            <div className="flex space-x-3">
              <button
                type="submit"
                disabled={loading}
                className="flex-1 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-400 text-white px-6 py-3 rounded-xl font-medium transition-colors"
              >
                {loading
                  ? "Saving..."
                  : editingGoal
                    ? "Update Goal"
                    : "Create Goal"}
              </button>
              <button
                type="button"
                onClick={resetForm}
                className="px-6 py-3 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-xl font-medium transition-colors"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-5 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-800 flex items-center">
            <FontAwesomeIcon icon={faFilter} className="mr-2 text-purple-600" />
            Filter Goals
          </h3>
          <button
            onClick={() => {
              setFilterStatus("all");
              setFilterCategory("all");
              setFilterUser("all");
              setSearchQuery("");
            }}
            className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm font-medium transition-colors flex items-center"
          >
            <FontAwesomeIcon icon={faTimes} className="mr-2" />
            Clear All
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <FontAwesomeIcon icon={faSearch} className="mr-2" />
              Search
            </label>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
              placeholder="Search goals..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <FontAwesomeIcon icon={faUser} className="mr-2" />
              User
            </label>
            <select
              value={filterUser}
              onChange={(e) => setFilterUser(e.target.value)}
              className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
            >
              <option value="all">All Users</option>
              {users.map((user) => (
                <option key={user.value} value={user.value}>
                  {user.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <FontAwesomeIcon icon={faFilter} className="mr-2" />
              Status
            </label>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
            >
              <option value="all">All Statuses</option>
              {statuses.map((stat) => (
                <option key={stat.value} value={stat.value}>
                  {stat.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <FontAwesomeIcon icon={faFilter} className="mr-2" />
              Category
            </label>
            <select
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
            >
              <option value="all">All Categories</option>
              {categories.map((cat) => (
                <option key={cat.value} value={cat.value}>
                  {cat.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Results
            </label>
            <div className="px-4 py-2 bg-purple-50 border-2 border-purple-200 rounded-lg text-purple-700 font-semibold text-center">
              {filteredGoals.length}{" "}
              {filteredGoals.length === 1 ? "goal" : "goals"}
            </div>
          </div>
        </div>
      </div>

      {/* Active Goals List */}
      {!loading && activeGoals.length > 0 && (
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-2xl font-bold text-gray-900 flex items-center">
              <FontAwesomeIcon
                icon={faBullseye}
                className="mr-3 text-purple-600"
              />
              Active Goals
              <span className="ml-3 px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm font-semibold">
                {activeGoals.length}
              </span>
            </h3>
          </div>
          <div className="space-y-4">
            {activeGoals.map((goal) => renderGoalCard(goal, false))}
          </div>
        </div>
      )}

      {/* Completed Goals Section */}
      {!loading && completedGoals.length > 0 && (
        <div className="mb-8">
          <div
            onClick={() => setShowCompletedSection(!showCompletedSection)}
            className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-5 mb-4 cursor-pointer hover:shadow-lg transition-all border-2 border-green-200"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="bg-green-600 p-3 rounded-xl">
                  <FontAwesomeIcon
                    icon={faTrophy}
                    className="text-white text-xl"
                  />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 flex items-center">
                    Completed Goals
                    <span className="ml-3 px-3 py-1 bg-green-600 text-white rounded-full text-sm font-semibold">
                      {completedGoals.length}
                    </span>
                  </h3>
                  <p className="text-gray-600 text-sm">
                    Click to {showCompletedSection ? "hide" : "view"} completed
                    goals
                  </p>
                </div>
              </div>
              <FontAwesomeIcon
                icon={showCompletedSection ? faChevronUp : faChevronDown}
                className="text-2xl text-green-600"
              />
            </div>
          </div>

          {showCompletedSection && (
            <div className="space-y-4 animate-fadeIn">
              {completedGoals.map((goal) => renderGoalCard(goal, true))}
            </div>
          )}
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="text-center py-12">
          <FontAwesomeIcon
            icon={faSpinner}
            spin
            className="text-4xl text-purple-600"
          />
          <p className="mt-4 text-gray-600">Loading goals...</p>
        </div>
      )}

      {/* Empty State */}
      {!loading && filteredGoals.length === 0 && (
        <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-12 text-center">
          <FontAwesomeIcon
            icon={faBullseye}
            className="text-6xl text-gray-300 mb-4"
          />
          <h3 className="text-xl font-bold text-gray-900 mb-2">
            No Goals Found
          </h3>
          <p className="text-gray-600 mb-6">
            {searchQuery ||
            filterStatus !== "all" ||
            filterCategory !== "all" ||
            filterUser !== "all"
              ? "Try adjusting your filters"
              : "Start by creating your first goal"}
          </p>
          {!showForm && (
            <button
              onClick={() => setShowForm(true)}
              className="px-6 py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium transition-colors"
            >
              <FontAwesomeIcon icon={faPlus} className="mr-2" />
              Create Your First Goal
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default GoalsManagement;
